import React from 'react';

/**
 * URL regex: matches http://, https://, and www. (with optional https://)
 * Only allows http/https for security.
 */
const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)|(www\.[^\s<>"{}|\\^`[\]]+)/gi;

function isValidUrl(url: string): boolean {
  const normalized = url.startsWith('www.') ? `https://${url}` : url;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

interface LinkifiedTextProps {
  text: string;
  className?: string;
  /** If true, preserve newlines (whitespace-pre-wrap) */
  preserveNewlines?: boolean;
  as?: 'span' | 'p';
}

/**
 * Renders text with URLs as clickable links.
 * Opens in new tab with rel="noopener noreferrer".
 * Only allows http/https URLs.
 */
export function LinkifiedText({
  text,
  className = '',
  preserveNewlines = true,
  as: Component = 'p',
}: LinkifiedTextProps) {
  const normalized = typeof text !== 'string' ? '' : text;
  if (!normalized) return <Component className={className} />;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);

  while ((match = regex.exec(normalized)) !== null) {
    const fullMatch = match[0];
    const href = fullMatch.startsWith('www.') ? `https://${fullMatch}` : fullMatch;

    if (!isValidUrl(href)) {
      parts.push(normalized.slice(lastIndex, match.index + fullMatch.length));
      lastIndex = match.index + fullMatch.length;
      continue;
    }

    if (match.index > lastIndex) {
      parts.push(normalized.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-purple-600 hover:underline break-all"
      >
        {fullMatch}
      </a>
    );
    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < normalized.length) {
    parts.push(normalized.slice(lastIndex));
  }

  return (
    <Component
      className={`${preserveNewlines ? 'whitespace-pre-wrap' : ''} ${className}`.trim()}
    >
      {parts.map((part, i) =>
        typeof part === 'string' ? (
          <React.Fragment key={i}>{part}</React.Fragment>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </Component>
  );
}
